import type { ButtonHTMLAttributes, ReactNode } from "react";

import { cn } from "../lib/cn.js";

/**
 * The button, in two variants.
 *
 * Ported from the `.btn` / `.btn-primary` classes this package replaced. The utility strings are
 * shadcn/ui's, with two substitutions carried over: `rounded-control` for `rounded-md`, since
 * GLADIS has a radius token, and the translucent neutrals from tokens.css — an opaque fill would
 * punch a rectangle out of the animated background these controls sit on.
 *
 * The DEFAULT is shadcn's `outline` variant, not its `default`. Most buttons in GLADIS are
 * "Cancel", "Skip the rest", "Sign out" — secondary by nature. The primary action is the exception
 * and asks for it by name, which keeps the common case honest.
 */

const BASE = [
  "inline-flex h-9 shrink-0 cursor-pointer items-center justify-center gap-2",
  "rounded-control px-4 py-2 text-sm font-medium whitespace-nowrap",
  "transition-all outline-none",
  // Every control carries its own focus ring. A global `:where(...)` rule used to do this for the
  // whole application; moving it here makes keyboard visibility a property of the component, so a
  // consumer cannot render an unreachable control by forgetting a utility.
  "focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50",
  "disabled:pointer-events-none disabled:opacity-50",
].join(" ");

const VARIANTS = {
  outline: "border border-input bg-input/30 text-ink hover:bg-accent",
  primary: "border border-transparent bg-primary text-primary-foreground hover:bg-primary/90",
} as const;

export type ButtonVariant = keyof typeof VARIANTS;

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  /**
   * Work is in flight: shows a spinner, disables the control, and sets `aria-busy`.
   *
   * ==========================================================================================
   * THE LABEL DOES NOT CHANGE, AND THAT IS THE DECISION IN THIS PROP
   * ==========================================================================================
   *
   * The obvious implementation swaps the text for "Sending…", and it breaks two things at once.
   * The button's WIDTH changes, so the form jumps under the cursor at the moment the member has
   * just committed to something. And the ACCESSIBLE NAME changes, which is worse than it looks
   * here: 47 e2e locators reach controls by name, and a spec that clicks "Email me a link" and
   * then asserts against it would be asserting on an element that renamed itself mid-flight.
   *
   * So the spinner is added BEFORE the label and the label stays put. `BASE` already sets `gap-2`,
   * so it slots in beside the text without a layout of its own.
   *
   * ==========================================================================================
   * DISABLING IS PART OF IT, NOT A SEPARATE PROP THE CALLER MUST REMEMBER
   * ==========================================================================================
   *
   * A pending button that can still be pressed is a double submit, and the sign-in form is exactly
   * where that matters — two magic links, of which only one works. `disabled` is OR-ed rather than
   * overwritten, so a caller that disables for its own reasons keeps that reason.
   */
  pending?: boolean;
}

export function Button({
  variant = "outline",
  className,
  type = "button",
  pending = false,
  disabled = false,
  children,
  ...rest
}: ButtonProps): ReactNode {
  // `type` defaults to "button". HTML defaults it to "submit", which means a button added to a form
  // for an unrelated purpose silently submits it — a bug this package should not make possible.
  return (
    <button
      type={type}
      disabled={disabled || pending}
      // The state, for anyone not looking at the spinner. `aria-busy` on the control itself is what
      // a screen reader reads; the glyph is decorative and says so.
      {...(pending ? { "aria-busy": true } : {})}
      className={cn(BASE, VARIANTS[variant], className)}
      {...rest}
    >
      {pending ? <Spinner /> : null}
      {children}
    </button>
  );
}

/**
 * The in-flight glyph: a ring with a gap, rotating.
 *
 * INLINE SVG rather than an icon package, which is this package's standing rule — `Dialog`'s close
 * control and `FilterSelect`'s chevron make the same call. Two shapes are not worth a dependency.
 *
 * `motion-reduce:animate-none` leaves a static ring for a reader who has asked for less motion. It
 * still reads as a change of state — the button is dimmed, the glyph is new, and `aria-busy` is set
 * — so nothing is conveyed by the rotation alone. `@gladis/ui/motion`'s `useMotionDisabled` is for
 * DECORATIVE motion (the background waves, the nav icons) and would be the wrong instrument here:
 * this is a status indicator, and it is a CSS media query rather than a hook so that a button in
 * flight costs no subscription.
 */
function Spinner(): ReactNode {
  return (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
      className="size-4 shrink-0 animate-spin motion-reduce:animate-none"
    >
      <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeOpacity="0.25" strokeWidth="2" />
      <path
        d="M14.5 8a6.5 6.5 0 0 0-6.5-6.5"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}
